import React, { useEffect, useState, useMemo } from "react";
import { Formik, getIn } from "formik";
import { Autocomplete, IconButton, Box, Grid, Grid2, Dialog, DialogActions, DialogContent, DialogContentText, DialogTitle, Typography, useTheme, Button, TextField, Stack, CircularProgress, Divider, FormHelperText } from "@mui/material";
import * as Yup from "yup";
import dayjs from "dayjs";
import { useParams } from "react-router-dom";
import { useUpdateDataAlertMutation } from "store/slices/apis/dataAlertManagementSlice";
import { IoMdClose } from "react-icons/io";
import { DatePicker, LocalizationProvider } from "@mui/x-date-pickers";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import { useGetAllRoleQuery } from "store/slices/apis/rolePackageSlice";

const intervals = [
    { label: "Daily", value: "daily" },
    { label: "Weekly", value: "weekly" },
    { label: "Monthly", value: "monthly" },
    { label: "When Change Occurs", value: "change" },
];

const repeats = [
    { label: "Daily", value: "daily" },
    { label: "Weekly", value: "weekly" },
];

const weekdays = [
    { label: "Monday", value: "monday" },
    { label: "Tuesday", value: "tuesday" },
    { label: "Wednesday", value: "wednesday" },
    { label: "Thursday", value: "thursday" },
    { label: "Friday", value: "friday" },
    { label: "Saturday", value: "saturday" },
    { label: "Sunday", value: "sunday" },
];

const months = [
    { label: "January", value: "january" },
    { label: "February", value: "february" },
    { label: "March", value: "march" },
    { label: "April", value: "april" },
    { label: "May", value: "may" },
    { label: "June", value: "june" },
    { label: "July", value: "july" },
    { label: "August", value: "august" },
    { label: "September", value: "september" },
    { label: "October", value: "october" },
    { label: "November", value: "november" },
    { label: "December", value: "december" },
];

const filterOptions = [
    { label: "Weekly", value: "weekly" },
    { label: "Monthly", value: "monthly" },
    { label: "Quaterly", value: "quaterly" },
    { label: "Date Range", value: "daterange" },
];

const EditReportData = (props) => {
    const theme = useTheme();
    const { projectId } = useParams();
    const {
        open,
        onClose,
        cancelIssue,
        openNotification,
        data,
        isViewIssueModal,
        refetch,
        columns,
    } = props;

    const { data: allRole } = useGetAllRoleQuery({
        projectId: projectId,
    });

    const roleData = allRole?.data?.map((item) => ({
        label: item.displayName,
        value: item.id,
    })) || [];

    const [editDataAlert, { isLoading }] = useUpdateDataAlertMutation();

    const handleClose = () => { };

    const getValidationSchema = (columnFilters = [], columns = [], currentFilterOptions = {}) => {
        const filterOptionShape = {};
        const filterDayRangeShape = {};

        columnFilters.forEach((filter) => {
            const column = columns.find((c) => c.accessorKey === filter.id);
            if (column?.dataType === 'date') {
                // 1. Filter date is required
                filterOptionShape[filter.id] = Yup.string()
                    .required("Filter date is required")
                    .oneOf(["Weekly", "Monthly", "Quaterly", "Date Range"]);

                // 2. If Date Range, then start and end days are required
                filterDayRangeShape[filter.id] = Yup.object().shape({
                    start: Yup.number()
                        .nullable()
                        .when(
                            `$filterOptions.${filter.id}`,
                            {
                                is: "Date Range",
                                then: (schema) => schema.required("Start day is required").min(1).max(31),
                                otherwise: (schema) => schema,
                            }
                        ),

                    end: Yup.number()
                        .nullable()
                        .when(
                            `$filterOptions.${filter.id}`,
                            {
                                is: "Date Range",
                                then: (schema) =>
                                    schema
                                        .required("End day is required")
                                        .min(1)
                                        .max(31)
                                        .test(
                                            "endAfterStart",
                                            "End day must be after or equal to start day",
                                            function (val) {
                                                const start = this?.parent?.start;
                                                return !start || !val || val >= start;
                                            }
                                        ),
                                otherwise: (schema) => schema,
                            }
                        ),
                });
            }
        });

        return Yup.object().shape({
            report_name: Yup.string().required("Report name is required"),
            interval: Yup.string().required("Interval is required"),

            // 1. Daily → repeat required
            // 2. Weekly → repeat + weekday required
            // 3. Monthly → month + day required
            repeat: Yup.number()
                .typeError("Repeat must be a number")
                .when("interval", {
                    is: (val) => val === "Daily" || val === "Weekly",
                    then: (schema) =>
                        schema
                            .required("Repeat is required")
                            .min(1, "Repeat must be at least 1"),
                    otherwise: (schema) => schema.notRequired(),
                }),

            weekday: Yup.string().when("interval", {
                is: (val) => val === "Weekly",
                then: () => Yup.string().required("Weekday is required"),
                otherwise: () => Yup.string().notRequired(),
            }),

            month: Yup.string().when("interval", {
                is: (val) => val === "Monthly",
                then: () => Yup.string().required("Month is required"),
                otherwise: () => Yup.string().notRequired(),
            }),

            day: Yup.number().when("interval", {
                is: (val) => val === "Monthly",
                then: () =>
                    Yup.number()
                        .typeError("Day must be a number")
                        .required("Day of Month is required")
                        .min(1, "Day must be at least 1")
                        .max(31, "Day can't exceed 31"),
                otherwise: () => Yup.number().notRequired(),
            }),

            start_date: Yup.string().required("Start Date is required"),
            //end_date: Yup.string().required("End Date is required"),
            recipient: Yup.array().of(Yup.string()).min(1, "Recipient is required").required("Recipient is required"),

            // add dynamic fields
            filterOptions: Yup.object().shape(filterOptionShape),
            filterDayRange: Yup.object().shape(filterDayRangeShape),
        });
    };

    const handleSubmit = async (values, { setSubmitting, resetForm }) => {

        let reqBody = {
            projectId: projectId,
            id: data?.id,
            dataMetric: "Report",
            status: "Active",
            AlertName: "Reporting",
            ValueType: "value",
            interval: values?.interval,
            repeat: values?.interval === 'Daily' || values?.interval === 'Weekly' ? values?.repeat : 0,
            day: values?.interval === 'Weekly' ? values?.weekday : '',
            //month: values?.interval === 'Monthly' ? values?.month : '',
            date: values?.interval === 'Monthly' ? values?.day : 0,
            roles: values?.recipient,

            startDate:
                values?.start_date && dayjs(values.start_date).isValid()
                    ? dayjs(values.start_date).format("YYYY-MM-DD") + "T00:00:00.000Z"
                    : null,
            endDate:
                values?.end_date && dayjs(values.end_date).isValid()
                    ? dayjs(values.end_date).format("YYYY-MM-DD") + "T00:00:00.000Z"
                    : null,
        };

        const existingFilters = values.allFilters || [];
        const existingSorting = values.sorting || [];

        const filterMap = new Map();
        existingFilters.forEach((f) => {
            filterMap.set(f.column, { ...f });
        });

        Object.entries(values.filterOptions || {}).forEach(([columnId, filterType]) => {
            if (filterType === "Date Range" && values.filterDayRange?.[columnId]) {
                const newStart = values.filterDayRange[columnId]?.start;
                const newEnd = values.filterDayRange[columnId]?.end;

                if (newStart != null && newEnd != null) {
                    filterMap.set(columnId, {
                        column: columnId,
                        type: "Date",
                        operator: "Between",
                        value: [newStart, newEnd],
                    });
                }
            } else if (["Weekly", "Monthly", "Quaterly"].includes(filterType)) {
                filterMap.set(columnId, {
                    column: columnId,
                    type: "Date",
                    operator: "Equal",
                    value: filterType,
                });
            }
        });

        const updatedFilters = Array.from(filterMap.values());

        let FilterJson = {
            module: values?.module,
            reportname: values?.report_name,
            filters: updatedFilters,
            sorting: existingSorting,
        };


        const payload = {
            ...reqBody,
            FilterJson: JSON.stringify(FilterJson),
        };

        try {
            const response = await editDataAlert({ id: data?.id, body: payload }).unwrap();
            if (response?.status == "success") {
                openNotification("Report updated successfully", "success");
                resetForm();
                onClose();
                //setSubmitting(false);
                //setOpenCustomDialog(false);
                handleClose();
            } else {
                openNotification("Error in report updation");
            }
        } catch (error) {
            openNotification(error?.error, "error");
        } finally {
            setSubmitting(false);
        }
    };

    const validationSchema = useMemo(
        () =>
            getValidationSchema(
                data.columnFilters,   // ⬅ date filter list
                columns               // ↳ your MRT columns
            ),
        [data.columnFilters, columns]
    );

    return (
        <React.Fragment>
            <DialogContent>
                <Formik
                    initialValues={data.initialFormData}
                    validationSchema={validationSchema}
                    onSubmit={handleSubmit}
                    enableReinitialize
                >
                    {({
                        values,
                        errors,
                        touched,
                        handleChange,
                        handleBlur,
                        handleSubmit,
                        isSubmitting,
                        setFieldValue,
                        setTouched,
                        setFieldTouched,
                        validateForm,
                    }) => {
                        return (
                            <>
                                <Dialog
                                    open={open}
                                    onClose={onClose}
                                    maxWidth={false}
                                    PaperProps={{
                                        sx: {
                                            width: { xs: "95vw", md: "84vw" },
                                            height: { xs: "95vh", md: "84vh" },
                                            p: 2,
                                            display: "flex",
                                            flexDirection: "column",
                                        },
                                    }}
                                >
                                    <DialogTitle sx={{ padding: "0px 0px 10px 15px", color: theme?.customSettings?.themeColor, fontSize: "24px" }}>
                                        Edit Report
                                    </DialogTitle>
                                    <IconButton
                                        aria-label="close"
                                        onClick={onClose}
                                        sx={(theme) => ({
                                            position: "absolute",
                                            right: 16,
                                            top: 16,
                                            color: theme.palette.grey[500],
                                        })}
                                    >
                                        <IoMdClose style={{ fontSize: "20px" }} />
                                    </IconButton>
                                    <form onSubmit={handleSubmit}
                                        style={{ display: "flex", flex: 1, flexDirection: "column", overflow: "hidden" }}
                                    >
                                        <Box
                                            sx={{
                                                flex: 1,
                                                overflow: "hidden",
                                                display: "flex",
                                                flexDirection: { xs: "column", md: "row" },
                                                gap: 2,
                                                p: 2,
                                            }}
                                        >
                                            <Box sx={{ flex: 4, display: "flex", flexDirection: "column", overflowY: "auto", }}>
                                                <Typography gutterBottom sx={{
                                                    position: "sticky",
                                                    top: 0,
                                                    zIndex: 2,
                                                    backgroundColor: "#FFFFFF", // or theme.palette.background.paper
                                                    color: theme?.customSettings?.themeColor,
                                                    fontSize: "18px",
                                                    pb: 1,
                                                }}>
                                                    Report Detail
                                                </Typography>
                                                {/* <Divider sx={{ w: 1 }} /> */}
                                                <Box sx={{ display: "flex", flexDirection: "column", gap: 1, mb: 2 }}>
                                                    <Typography sx={{ color: "#505258", mt: 1 }}>Report Name: </Typography>
                                                    <TextField
                                                        label="Report Name"
                                                        name="report_name"
                                                        value={values?.report_name}
                                                        onChange={(e) => setFieldValue("report_name", e.target.value)}
                                                        onBlur={() => setTouched({ ...touched, report_name: true })}
                                                        variant="filled"
                                                        InputLabelProps={{ shrink: true }}
                                                        InputProps={{
                                                            sx: {
                                                                fontSize: '14px',        // Input text font size
                                                                color: '#505258',        // Input text color
                                                                opacity: 0.6,
                                                                width: { xs: '95%', sm: '70%' },
                                                            },
                                                        }}
                                                    />
                                                    {touched?.report_name && errors?.report_name && (
                                                        <FormHelperText
                                                            sx={{ color: "#C52A1A", fontSize: "12px" }}
                                                        >{`${errors?.report_name}`}</FormHelperText>
                                                    )}

                                                    <Typography sx={{ color: "#505258", mt: 1 }}>Filters: </Typography>
                                                    {data.columnFilters?.length === 0 ? (
                                                        <Typography sx={{ fontSize: "14px", color: '#505258', opacity: "0.6", pl: 1 }}>No filters applied</Typography>
                                                    ) : (
                                                        data.columnFilters.map((filter, idx) => {
                                                            const col = columns.find((c) => c.accessorKey === filter.id) || {
                                                                accessorKey: filter.id,
                                                                header: filter.id,
                                                                dataType: "date",  // or "string" if unsure
                                                            };
                                                            const isDateField = col?.dataType === "date";
                                                            return (
                                                                <Box key={idx} sx={{ mb: 1 }}>
                                                                    <Typography key={idx} sx={{ fontSize: "14px", color: '#505258', opacity: "0.6", mb: 1 }}>
                                                                        • {col?.header || filter.id}: {filter.value?.toString()}
                                                                    </Typography>
                                                                    {isDateField && (
                                                                        <>
                                                                            <Autocomplete
                                                                                options={filterOptions}
                                                                                getOptionLabel={(option) => option?.label}
                                                                                isOptionEqualToValue={(option, value) => option.label === value.label}
                                                                                value={
                                                                                    values.filterOptions?.[filter.id]
                                                                                        ? { label: values.filterOptions[filter.id] }
                                                                                        : null
                                                                                }
                                                                                onChange={(_, newValue) => {
                                                                                    const selectedInterval = newValue ? newValue?.label : "";
                                                                                    setFieldValue(`filterOptions.${filter.id}`, selectedInterval);


                                                                                    if (selectedInterval !== "Date Range") {
                                                                                        // clear day range if switching away
                                                                                        setFieldValue(`filterDayRange.${filter.id}`, { start: "", end: "" });
                                                                                    }
                                                                                    setTimeout(() => {
                                                                                        setFieldTouched(`filterOptions.${filter.id}`, true, true); // force touch
                                                                                        validateForm(); // triggers Yup validation with updated context
                                                                                    }, 0);
                                                                                }}
                                                                                onBlur={() => {
                                                                                    setFieldTouched(`filterOptions.${filter.id}`, true, true);
                                                                                }}
                                                                                renderInput={(params) => (
                                                                                    <TextField
                                                                                        {...params}
                                                                                        label="Filter Date"
                                                                                        name={`filterOptions.${filter.id}`}
                                                                                        slotProps={{ inputLabel: { shrink: true } }}
                                                                                        variant="filled"
                                                                                        InputProps={{
                                                                                            ...params.InputProps,
                                                                                            sx: {
                                                                                                fontSize: '14px',        // Input text font size
                                                                                                color: '#505258',        // Input text color
                                                                                                opacity: 0.6,
                                                                                                width: { xs: '95%', sm: '70%' },
                                                                                            },
                                                                                        }}
                                                                                    />
                                                                                )}
                                                                                ListboxProps={{
                                                                                    sx: { fontSize: "14px", color: '#505258', opacity: 0.6, },
                                                                                }}
                                                                            />
                                                                            {getIn(touched, `filterOptions.${filter.id}`) &&
                                                                                getIn(errors, `filterOptions.${filter.id}`) && (
                                                                                    <FormHelperText sx={{ color: "#C52A1A", fontSize: "12px" }}>
                                                                                        {getIn(errors, `filterOptions.${filter.id}`)}
                                                                                    </FormHelperText>
                                                                                )}

                                                                            {values?.filterOptions?.[filter.id] === "Date Range" && (
                                                                                <Box sx={{ mt: 1, display: "flex", width: { xs: '95%', sm: '70%' }, gap: 2 }}>
                                                                                    {/* Start Day */}
                                                                                    <Autocomplete
                                                                                        options={Array.from({ length: 31 }, (_, i) => ({ label: `${i + 1}`, value: i + 1 }))}
                                                                                        getOptionLabel={(option) => option.label}
                                                                                        value={
                                                                                            values?.filterDayRange?.[filter.id]?.start
                                                                                                ? { label: `${values.filterDayRange[filter.id].start}` }
                                                                                                : null
                                                                                        }
                                                                                        onChange={(_, newValue) =>
                                                                                            setFieldValue(`filterDayRange.${filter.id}.start`, newValue?.value || "")
                                                                                        }
                                                                                        onBlur={() => {
                                                                                            setFieldTouched(`filterDayRange.${filter.id}.start`, true, true);
                                                                                        }}
                                                                                        renderInput={(params) => (
                                                                                            <TextField
                                                                                                {...params}
                                                                                                label="Start Day"
                                                                                                name={`filterDayRange.${filter.id}.start`}
                                                                                                variant="filled"
                                                                                                slotProps={{ inputLabel: { shrink: true } }}
                                                                                                helperText={
                                                                                                    getIn(touched, `filterDayRange.${filter.id}.start`) &&
                                                                                                        getIn(errors, `filterDayRange.${filter.id}.start`)
                                                                                                        ? (
                                                                                                            <span style={{ color: '#C52A1A' }}>
                                                                                                                {getIn(errors, `filterDayRange.${filter.id}.start`)}
                                                                                                            </span>
                                                                                                        )
                                                                                                        : ""
                                                                                                }
                                                                                                InputProps={{
                                                                                                    ...params.InputProps,
                                                                                                    sx: {
                                                                                                        fontSize: '14px',        // Input text font size
                                                                                                        color: '#505258',        // Input text color
                                                                                                        opacity: 0.6,

                                                                                                    },
                                                                                                }}
                                                                                            />
                                                                                        )}
                                                                                        sx={{ flex: 1 }}
                                                                                        ListboxProps={{
                                                                                            sx: { fontSize: "14px", color: '#505258', opacity: 0.6, },
                                                                                        }}
                                                                                    />
                                                                                    <Autocomplete
                                                                                        options={Array.from({ length: 31 }, (_, i) => ({ label: `${i + 1}`, value: i + 1 }))}
                                                                                        getOptionLabel={(option) => option.label}
                                                                                        value={
                                                                                            values?.filterDayRange?.[filter.id]?.end
                                                                                                ? { label: `${values.filterDayRange[filter.id].end}` }
                                                                                                : null
                                                                                        }
                                                                                        onChange={(_, newValue) =>
                                                                                            setFieldValue(`filterDayRange.${filter.id}.end`, newValue?.value || "")
                                                                                        }
                                                                                        onBlur={() => {
                                                                                            setFieldTouched(`filterDayRange.${filter.id}.end`, true, true);
                                                                                        }}
                                                                                        renderInput={(params) => (
                                                                                            <TextField
                                                                                                {...params}
                                                                                                label="End Day"
                                                                                                name={`filterDayRange.${filter.id}.end`}
                                                                                                variant="filled"
                                                                                                slotProps={{ inputLabel: { shrink: true } }}
                                                                                                helperText={
                                                                                                    getIn(touched, `filterDayRange.${filter.id}.end`) &&
                                                                                                        getIn(errors, `filterDayRange.${filter.id}.end`)
                                                                                                        ? (
                                                                                                            <span style={{ color: '#C52A1A' }}>
                                                                                                                {getIn(errors, `filterDayRange.${filter.id}.end`)}
                                                                                                            </span>
                                                                                                        )
                                                                                                        : ""
                                                                                                }
                                                                                                InputProps={{
                                                                                                    ...params.InputProps,
                                                                                                    sx: {
                                                                                                        fontSize: '14px',        // Input text font size
                                                                                                        color: '#505258',        // Input text color
                                                                                                        opacity: 0.6,

                                                                                                    },
                                                                                                }}
                                                                                            />
                                                                                        )}
                                                                                        sx={{ flex: 1 }}
                                                                                        ListboxProps={{
                                                                                            sx: { fontSize: "14px", color: '#505258', opacity: 0.6, },
                                                                                        }}
                                                                                    />
                                                                                </Box>
                                                                            )}

                                                                        </>
                                                                    )
                                                                    }
                                                                </Box>
                                                            );
                                                        })
                                                    )}
                                                </Box>
                                            </Box>

                                            <Box sx={{
                                                width: { xs: '100%', md: '1px' },            // Full width on mobile, 1px on desktop
                                                height: { xs: '1px', md: 'auto' },            // 1px height on mobile, auto height on desktop
                                                backgroundColor: '#ccc',
                                                my: { xs: 2, md: 0 },                         // vertical spacing on mobile
                                                mx: { xs: 0, md: 2 },                         // horizontal spacing on desktop
                                                flexShrink: 0,
                                            }} />

                                            <Box sx={{ flex: 6, display: "flex", flexDirection: "column", overflowY: "auto", p: 2, pt: 0 }}>
                                                <Typography gutterBottom sx={{
                                                    position: "sticky",
                                                    top: 0,
                                                    zIndex: 2,
                                                    backgroundColor: "#FFFFFF", // or theme.palette.background.paper
                                                    color: theme?.customSettings?.themeColor,
                                                    fontSize: "18px",
                                                    pb: 1,
                                                }}>
                                                    Schedule Detail
                                                </Typography>
                                                <Grid container spacing={2}>

                                                    <Grid item xs={12} md={6}>
                                                        <Typography sx={{ color: "#505258", mt: 1 }}>Interval: </Typography>
                                                        <Autocomplete
                                                            options={intervals}
                                                            getOptionLabel={(option) => option?.label}
                                                            value={
                                                                intervals.find(
                                                                    (option) => option.label === values?.interval
                                                                ) || null
                                                            }
                                                            onChange={(_, newValue) => {
                                                                const selectedInterval = newValue ? newValue.label : "";
                                                                setFieldValue("interval", selectedInterval);

                                                                // Reset 'repeat'
                                                                if (selectedInterval === "When Change Occurs") {
                                                                    setFieldValue("repeat", "");
                                                                }
                                                            }}
                                                            onBlur={() =>
                                                                setTouched({ ...touched, interval: true })
                                                            }
                                                            renderInput={(params) => (
                                                                <TextField
                                                                    {...params}
                                                                    label="Interval"
                                                                    name="interval"
                                                                    slotProps={{ inputLabel: { shrink: true } }}
                                                                    variant="filled"
                                                                    fullWidth
                                                                    InputProps={{
                                                                        ...params.InputProps,
                                                                        sx: {
                                                                            fontSize: '14px',        // Input text font size
                                                                            color: '#505258',        // Input text color
                                                                            opacity: 0.6,
                                                                            // width: "70%",
                                                                        },
                                                                    }}
                                                                />
                                                            )}
                                                            ListboxProps={{
                                                                sx: { fontSize: "14px" }, color: '#505258', opacity: 0.6,
                                                            }}
                                                        />
                                                        {touched?.interval && errors?.interval && (
                                                            <FormHelperText
                                                                sx={{ color: "#C52A1A", fontSize: "12px" }}
                                                            >{`${errors?.interval}`}</FormHelperText>
                                                        )}
                                                    </Grid>

                                                    {values?.interval === "Daily" ? (
                                                        // Repeat TextField when Interval is 'Daily'
                                                        <Grid item xs={12} md={6}>
                                                            <Typography sx={{ color: "#505258", mt: 1 }}>Repeat: </Typography>
                                                            <TextField
                                                                label="Reoccur every (Days)"
                                                                name="repeat"
                                                                value={values?.repeat}
                                                                onChange={(e) => setFieldValue("repeat", e.target.value)}
                                                                onBlur={() => setTouched({ ...touched, repeat: true })}
                                                                variant="filled"
                                                                fullWidth
                                                                slotProps={{ inputLabel: { shrink: true } }}
                                                                InputProps={{
                                                                    sx: {
                                                                        fontSize: '14px',        // Input text font size
                                                                        color: '#505258',        // Input text color
                                                                        opacity: 0.6,
                                                                    },
                                                                }}
                                                            />
                                                            {touched?.repeat && errors?.repeat && (
                                                                <FormHelperText
                                                                    sx={{ color: "#C52A1A", fontSize: "12px" }}
                                                                >{`${errors?.repeat}`}</FormHelperText>
                                                            )}
                                                        </Grid>
                                                    ) :
                                                        values?.interval === "Weekly" ? (
                                                            <>
                                                                {/* Repeat TextField when Interval is 'Weekly'*/}
                                                                <Grid item xs={12} md={6}>
                                                                    <Typography sx={{ color: "#505258", mt: 1 }}>Repeat: </Typography>
                                                                    <TextField
                                                                        label="Reoccur every (Weeks)"
                                                                        name="repeat"
                                                                        value={values?.repeat}
                                                                        onChange={(e) => setFieldValue("repeat", e.target.value)}
                                                                        onBlur={() => setTouched({ ...touched, repeat: true })}
                                                                        variant="filled"
                                                                        fullWidth
                                                                        slotProps={{ inputLabel: { shrink: true } }}
                                                                        InputProps={{
                                                                            sx: {
                                                                                fontSize: '14px',        // Input text font size
                                                                                color: '#505258',        // Input text color
                                                                                opacity: 0.6,
                                                                            },
                                                                        }}
                                                                    />
                                                                    {touched?.repeat && errors?.repeat && (
                                                                        <FormHelperText
                                                                            sx={{ color: "#C52A1A", fontSize: "12px" }}
                                                                        >{`${errors?.repeat}`}</FormHelperText>
                                                                    )}
                                                                </Grid>

                                                                {/* Weekday Dropdown */}
                                                                <Grid item xs={12} md={6}>
                                                                    <Typography sx={{ color: "#505258", mt: 1 }}>Weekday: </Typography>
                                                                    <Autocomplete
                                                                        options={weekdays}
                                                                        getOptionLabel={(option) => option?.label}
                                                                        value={
                                                                            weekdays.find((opt) => opt.label === values?.weekday) || null
                                                                        }
                                                                        onChange={(_, newValue) =>
                                                                            setFieldValue("weekday", newValue ? newValue.label : "")
                                                                        }
                                                                        isOptionEqualToValue={(option, value) => option.value === value.value}
                                                                        renderInput={(params) => (
                                                                            <TextField
                                                                                {...params}
                                                                                label="On Day"
                                                                                name="weekday"
                                                                                variant="filled"
                                                                                slotProps={{ inputLabel: { shrink: true } }}
                                                                                fullWidth
                                                                                InputProps={{
                                                                                    ...params.InputProps,
                                                                                    sx: {
                                                                                        fontSize: '14px',        // Input text font size
                                                                                        color: '#505258',        // Input text color
                                                                                        opacity: 0.6,
                                                                                    },
                                                                                }}
                                                                            />
                                                                        )}
                                                                        ListboxProps={{
                                                                            sx: { fontSize: "14px" }, color: '#505258', opacity: 0.6,
                                                                        }}
                                                                    />
                                                                    {touched?.weekday && errors?.weekday && (
                                                                        <FormHelperText
                                                                            sx={{ color: "#C52A1A", fontSize: "12px" }}
                                                                        >{`${errors?.weekday}`}</FormHelperText>
                                                                    )}
                                                                </Grid>
                                                            </>
                                                        ) :
                                                            values?.interval === "Monthly" ? (
                                                                <>
                                                                    {/* Month Dropdown */}
                                                                    <Grid item xs={12} md={6}>
                                                                        <Typography sx={{ color: "#505258", mt: 1 }}>Month: </Typography>
                                                                        <Autocomplete
                                                                            options={months}
                                                                            getOptionLabel={(option) => option.label}
                                                                            value={
                                                                                months.find(
                                                                                    (option) => option.label === values?.month
                                                                                ) || null
                                                                            }
                                                                            onChange={(_, newValue) =>
                                                                                setFieldValue("month", newValue ? newValue.label : "")
                                                                            }
                                                                            isOptionEqualToValue={(option, value) => option.value === value.value}
                                                                            renderInput={(params) => (
                                                                                <TextField
                                                                                    {...params}
                                                                                    label="Month"
                                                                                    name="month"
                                                                                    variant="filled"
                                                                                    slotProps={{ inputLabel: { shrink: true } }}
                                                                                    fullWidth
                                                                                    InputProps={{
                                                                                        ...params.InputProps,
                                                                                        sx: {
                                                                                            fontSize: '14px',        // Input text font size
                                                                                            color: '#505258',        // Input text color
                                                                                            opacity: 0.6,
                                                                                        },
                                                                                    }}
                                                                                />
                                                                            )}
                                                                            ListboxProps={{
                                                                                sx: { fontSize: "14px" }, color: '#505258', opacity: 0.6,
                                                                            }}
                                                                        />
                                                                        {touched?.month && errors?.month && (
                                                                            <FormHelperText
                                                                                sx={{ color: "#C52A1A", fontSize: "12px" }}
                                                                            >{`${errors?.month}`}</FormHelperText>
                                                                        )}
                                                                    </Grid>

                                                                    {/* Day Dropdown (1–31) */}
                                                                    <Grid item xs={12} md={6}>
                                                                        <Typography sx={{ color: "#505258", mt: 1 }}>Day of Month: </Typography>
                                                                        <Autocomplete
                                                                            options={Array.from({ length: 31 }, (_, i) => i + 1).map((day) => ({
                                                                                label: `Day ${day}`,
                                                                                value: day,
                                                                            }))}
                                                                            getOptionLabel={(option) => option.label}
                                                                            value={
                                                                                Array.from({ length: 31 }, (_, i) => i + 1)
                                                                                    .map((day) => ({ label: `Day ${day}`, value: day }))
                                                                                    .find((opt) => opt.value === values?.day) || null
                                                                            }
                                                                            onChange={(_, newValue) =>
                                                                                setFieldValue("day", newValue ? newValue.value : "")
                                                                            }
                                                                            isOptionEqualToValue={(option, value) => option.value === value.value}
                                                                            renderInput={(params) => (
                                                                                <TextField
                                                                                    {...params}
                                                                                    label="Day of Month"
                                                                                    name="day"
                                                                                    variant="filled"
                                                                                    slotProps={{ inputLabel: { shrink: true } }}
                                                                                    fullWidth
                                                                                    InputProps={{
                                                                                        ...params.InputProps,
                                                                                        sx: {
                                                                                            fontSize: '14px',        // Input text font size
                                                                                            color: '#505258',        // Input text color
                                                                                            opacity: 0.6,
                                                                                        },
                                                                                    }}
                                                                                />
                                                                            )}
                                                                            ListboxProps={{
                                                                                sx: { fontSize: "14px" }, color: '#505258', opacity: 0.6,
                                                                            }}
                                                                        />
                                                                        {touched?.day && errors?.day && (
                                                                            <FormHelperText
                                                                                sx={{ color: "#C52A1A", fontSize: "12px" }}
                                                                            >{`${errors?.day}`}</FormHelperText>
                                                                        )}
                                                                    </Grid>
                                                                </>
                                                            ) : (
                                                                <Grid item xs={12} md={6}>
                                                                    <Typography sx={{ color: "#505258", mt: 1 }}>Repeat: </Typography>
                                                                    <Autocomplete
                                                                        options={repeats}
                                                                        getOptionLabel={(option) => option?.label}
                                                                        value={
                                                                            repeats.find(
                                                                                (option) => option.value === values?.repeat
                                                                            ) || null
                                                                        }
                                                                        onChange={(_, newValue) =>
                                                                            setFieldValue(
                                                                                "repeat",
                                                                                newValue ? newValue.label : ""
                                                                            )
                                                                        }
                                                                        isOptionEqualToValue={(option, value) => option.value === value.value}
                                                                        //   onBlur={() =>
                                                                        //     setTouched({ ...touched, interval: true })
                                                                        //   }
                                                                        renderInput={(params) => (
                                                                            <TextField
                                                                                {...params}
                                                                                label="Repeat"
                                                                                name="repeat"
                                                                                slotProps={{ inputLabel: { shrink: true } }}
                                                                                variant="filled"
                                                                                fullWidth
                                                                                InputProps={{
                                                                                    ...params.InputProps,
                                                                                    sx: {
                                                                                        fontSize: '14px',        // Input text font size
                                                                                        color: '#505258',        // Input text color
                                                                                        opacity: 0.6,
                                                                                    },
                                                                                }}
                                                                            />
                                                                        )}
                                                                        ListboxProps={{
                                                                            sx: { fontSize: "14px" }, color: '#505258', opacity: 0.6,
                                                                        }}
                                                                        // Disable 'repeat'
                                                                        disabled={values?.interval === 'When Change Occurs'}
                                                                    />
                                                                </Grid>
                                                            )}

                                                    <Grid item xs={12} md={6}>
                                                        <Typography sx={{ color: "#505258", mt: 1 }}>Start Date: </Typography>
                                                        <LocalizationProvider dateAdapter={AdapterDayjs}>
                                                            <DatePicker
                                                                label="Start Date"
                                                                value={
                                                                    values?.start_date
                                                                        ? dayjs(values.start_date)
                                                                        : null
                                                                }
                                                                format="DD-MM-YYYY"
                                                                minDate={dayjs()}
                                                                maxDate={values?.end_date ? dayjs(values.end_date) : undefined}
                                                                onChange={(newValue) => {
                                                                    handleChange({
                                                                        target: {
                                                                            name: "start_date",
                                                                            value: newValue ? newValue : "",
                                                                        },
                                                                    });
                                                                }}
                                                                onBlur={() =>
                                                                    setTouched({
                                                                        ...touched,
                                                                        start_date: true,
                                                                    })
                                                                }
                                                                slotProps={{
                                                                    textField: {
                                                                        variant: "filled",
                                                                        fullWidth: true,
                                                                        InputLabelProps: {
                                                                            style: { fontSize: 14 },
                                                                            shrink: true,
                                                                        },
                                                                        inputProps: {
                                                                            style: { height: "20px" },
                                                                        },
                                                                        //placeholder: "DD/MM/YYYY",
                                                                        sx: {
                                                                            // width: "70%",
                                                                            "& .MuiInputBase-input": {
                                                                                fontSize: 14,            // input date text size
                                                                                color: "#505258",        // input date text color
                                                                                opacity: 0.6,
                                                                            },
                                                                            input: {
                                                                                "&::placeholder": {
                                                                                    fontSize: 14,
                                                                                    color: "#505258",
                                                                                    opacity: 0.6,
                                                                                },
                                                                            },
                                                                            "& .MuiFormLabel-asterisk": {
                                                                                color: "red",
                                                                            },
                                                                        },
                                                                        onKeyDown: (e) => e.preventDefault(),
                                                                    },
                                                                    inputLabel: { shrink: true },
                                                                }}
                                                            />
                                                            {touched.start_date && errors.start_date && (
                                                                <FormHelperText sx={{ color: "#C52A1A", fontSize: "12px" }}>
                                                                    {errors.start_date}
                                                                </FormHelperText>
                                                            )}
                                                        </LocalizationProvider>
                                                    </Grid>

                                                    <Grid item xs={12} md={6}>
                                                        <Typography sx={{ color: "#505258", mt: 1 }}>End Date: </Typography>
                                                        <LocalizationProvider dateAdapter={AdapterDayjs}>
                                                            <DatePicker
                                                                label="End Date"
                                                                value={
                                                                    values?.end_date
                                                                        ? dayjs(values.end_date)
                                                                        : null
                                                                }
                                                                format="DD-MM-YYYY"
                                                                minDate={values?.start_date ? dayjs(values.start_date) : dayjs()}
                                                                onChange={(newValue) => {
                                                                    handleChange({
                                                                        target: {
                                                                            name: "end_date",
                                                                            value: newValue ? newValue : "",
                                                                        },
                                                                    });
                                                                }}
                                                                // onBlur={() =>
                                                                //     setTouched({
                                                                //         ...touched,
                                                                //         end_date: true,
                                                                //     })
                                                                // }
                                                                slotProps={{
                                                                    textField: {
                                                                        variant: "filled",
                                                                        fullWidth: true,
                                                                        InputLabelProps: {
                                                                            style: { fontSize: 14 },
                                                                            shrink: true,
                                                                        },
                                                                        inputProps: {
                                                                            style: { height: "20px" },
                                                                        },
                                                                        //placeholder: "DD/MM/YYYY",
                                                                        sx: {
                                                                            "& .MuiInputBase-input": {
                                                                                fontSize: 14,            // input date text size
                                                                                color: "#505258",        // input date text color
                                                                                opacity: 0.6,
                                                                            },
                                                                            input: {
                                                                                "&::placeholder": {
                                                                                    fontSize: 14,
                                                                                    color: "#505258",
                                                                                },
                                                                            },
                                                                            "& .MuiFormLabel-asterisk": {
                                                                                color: "red",
                                                                            },
                                                                        },
                                                                        onKeyDown: (e) => e.preventDefault(),
                                                                    },
                                                                    inputLabel: { shrink: true },
                                                                }}
                                                            />
                                                            {/* {touched.end_date && errors.end_date && (
                                                                                         <FormHelperText sx={{ color: "#C52A1A", fontSize: "12px" }}>
                                                                                             {errors.end_date}
                                                                                         </FormHelperText>
                                                                                     )} */}
                                                        </LocalizationProvider>
                                                    </Grid>

                                                    <Grid item xs={12} md={6}>
                                                        <Typography sx={{ color: "#505258", mt: 1 }}>Recipient: </Typography>
                                                        <Autocomplete
                                                            multiple
                                                            options={roleData}
                                                            getOptionLabel={(option) => option?.label}
                                                            value={
                                                                roleData?.filter((option) =>
                                                                    values?.recipient?.includes(option.value)
                                                                ) || []
                                                            }
                                                            onChange={(_, newValue) =>
                                                                setFieldValue(
                                                                    "recipient",
                                                                    newValue ? newValue.map((item) => item.value) : []
                                                                )
                                                            }
                                                            onBlur={() =>
                                                                setTouched({ ...touched, recipient: true })
                                                            }
                                                            renderInput={(params) => (
                                                                <TextField
                                                                    {...params}
                                                                    label="Recipient"
                                                                    name="recipient"
                                                                    slotProps={{ inputLabel: { shrink: true } }}
                                                                    variant="filled"
                                                                    fullWidth
                                                                    InputProps={{
                                                                        ...params.InputProps,
                                                                        sx: {
                                                                            fontSize: '14px',        // Input text font size
                                                                            color: '#505258',        // Input text color
                                                                            opacity: 0.6,
                                                                            // width: "70%",
                                                                        },
                                                                    }}
                                                                />
                                                            )}
                                                            ListboxProps={{
                                                                sx: { fontSize: "14px" }, color: '#505258', opacity: 0.6,
                                                            }}
                                                        />
                                                        {touched?.recipient && errors?.recipient && (
                                                            <FormHelperText
                                                                sx={{ color: "#C52A1A", fontSize: "12px" }}
                                                            >{`${errors?.recipient}`}</FormHelperText>
                                                        )}
                                                    </Grid>

                                                </Grid>
                                            </Box>
                                        </Box>
                                        <Box
                                            sx={{
                                                display: "flex",
                                                justifyContent: "flex-end",
                                                mt: 2,
                                            }}
                                        >
                                            <Button
                                                onClick={async () => {
                                                    const touchedMap = {
                                                        report_name: true,
                                                        interval: true,
                                                        repeat: true,
                                                        weekday: true,
                                                        month: true,
                                                        day: true,
                                                        start_date: true,
                                                        end_date: true,
                                                        recipient: true,
                                                        filterOptions: {},
                                                        filterDayRange: {},
                                                    };

                                                    data.columnFilters.forEach((filter) => {
                                                        const col = columns.find((c) => c.accessorKey === filter.id) || {
                                                            accessorKey: filter.id,
                                                            header: filter.id,
                                                            dataType: "date",  // or "string" if unsure
                                                        };
                                                        if (col?.dataType === "date") {
                                                            touchedMap.filterOptions[filter.id] = true;
                                                            touchedMap.filterDayRange[filter.id] = { start: true, end: true };
                                                        }
                                                    });

                                                    await setTouched(touchedMap, true);
                                                    const formErrors = await validateForm();

                                                    if (Object.keys(formErrors).length === 0) {
                                                        handleSubmit();
                                                    }
                                                }}
                                                sx={{
                                                    px: 3,
                                                    mr: 2,
                                                    textTransform: "none",
                                                    color: "#FFFFFF",
                                                    backgroundColor: theme?.customSettings?.themeColor,
                                                    border: `1px solid ${theme?.customSettings?.themeColor}`,
                                                }}
                                            >
                                                Save
                                            </Button>
                                        </Box>
                                    </form>
                                </Dialog>
                            </>
                        )
                    }
                    }
                </Formik>
            </DialogContent>

        </React.Fragment>
    );

};

export default EditReportData;